import React, { FC, useEffect, useState } from 'react';
import { useLazyGetMemberModalsContactSelectQuery } from '@store/rtk/global';
import { idType, selectItemType } from '@src/types';
import { useAppSelector } from '@store/hooks';
import { selectContacts } from '@store/slices/AuthContacts/AuthContactsSlice';
import RideAddContactSkeleton from '@uikit/Loader/RideAddContact/RideAddContactSkeleton';
import { logger } from '@utils/logger';
import { FALLBACK_IMAGE, IMAGE_STORAGE_URL } from '@utils/constants';
import { handleImgError } from '@src/utils';

type authContactsAttachPropsType = {
  setAttachedContact: (id: idType) => Promise<idType>;
  dataTestId?: string;
};

type authContactsSelectType = selectItemType & { image: string };

const AuthContactsAttach: FC<authContactsAttachPropsType> = ({
  setAttachedContact,
  dataTestId,
}) => {
  const [getMemberModalsContactSelect, { isFetching }] =
    useLazyGetMemberModalsContactSelectQuery();

  const { contactsTableData } = useAppSelector(selectContacts);

  const [authContactsSuggestions, setAuthContactsSuggestions] = useState<
    Array<authContactsSelectType>
  >([]);

  useEffect(() => {
    (async () => {
      try {
        const contactsSelect =
          (await getMemberModalsContactSelect({}).unwrap()) || [];

        if (contactsTableData?.rows.length) {
          setAuthContactsSuggestions(
            contactsTableData?.rows.reduce(
              (
                acc: Array<authContactsSelectType>,
                next: authContactsSelectType
              ) => {
                return acc.filter(({ id }) => id !== next.id);
              },
              contactsSelect
            )
          );
        } else {
          setAuthContactsSuggestions(contactsSelect);
        }
      } catch (err) {
        logger.log(err);
      }
    })();
  }, [contactsTableData]);

  const handleAttachOrRemoveContact = async (id: idType) => {
    await setAttachedContact(id).then((res) => {
      if (res) {
        setAuthContactsSuggestions((prev) => {
          return prev.filter((item) => item.id === res);
        });
      }
    });
  };

  if (isFetching) {
    return (
      <div className="contact_attach_dropdown" id="dropdown">
        <RideAddContactSkeleton />
      </div>
    );
  }

  return (
    <div
      data-test-id="c-auth-contacts-attach"
      className="contact_attach_dropdown"
      id="dropdown"
    >
      {!authContactsSuggestions.length ? (
        <div className="no_contacts">
          <span>There are no contacts.</span>
        </div>
      ) : (
        authContactsSuggestions.map(
          ({ id, image, value, label, sms }, index) => (
            <div
              key={id}
              id={`${id}`}
              className="select_option"
              onClick={() => handleAttachOrRemoveContact(id)}
              data-test-id={`${dataTestId}-auth-contact-option`}
              style={
                index !== authContactsSuggestions.length - 1
                  ? { borderBottom: '2px solid #E5E5E5' }
                  : {}
              }
            >
              <div className="contact_image_wrapper">
                <img
                  src={image ? `${IMAGE_STORAGE_URL}/${image}` : FALLBACK_IMAGE}
                  alt="auth contact image"
                  className="uploading-image"
                  onError={handleImgError}
                  data-test-id={`${dataTestId}-auth-contact-option-image`}
                />
              </div>
              <div
                className="contact_wrapper"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div className="contact_info">
                  <div
                    className="contact_name"
                    data-test-id={`${dataTestId}-auth-contact-option-contact-name`}
                  >
                    {value}
                  </div>
                  <div
                    className="contact_jobTitle"
                    data-test-id={`${dataTestId}-auth-contact-option-contact-jobTitle`}
                  >
                    {label}
                  </div>
                </div>
                <div
                  className="contact_sms"
                  data-test-id={`${dataTestId}-auth-contact-option-contact-sms`}
                >
                  SMS Opt {sms ? 'In' : 'Out'}
                </div>
              </div>
            </div>
          )
        )
      )}
    </div>
  );
};

export default AuthContactsAttach;
